// ProjectLinkManager.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosDefault from 'axios';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  MessageBar,
  MessageBarType,
  Spinner,
  TooltipHost,
  Dialog,
  DialogType,
  DialogFooter,
  TagPicker,
  useTheme,
  Label,
  ActionButton,
  mergeStyleSets,
  Separator
} from '@fluentui/react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const styles = mergeStyleSets({
  card: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 12 },
  header: { marginBottom: 8 },
  smallNote: { opacity: .7 }
});

const ProjectLinkManager = ({
  entityType = 'person', // 'person' or 'proposal'
  entityId,
  axiosInstance,
}) => {
  const axios = axiosInstance || axiosDefault;
  const theme = useTheme();

  const [linked, setLinked] = useState([]);   // projects currently linked
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [isManageOpen, setIsManageOpen] = useState(false);
  const [toAdd, setToAdd] = useState([]);     // TagPicker items (projects to add)

  const debounceRef = useRef(null);

  // Resolve endpoints based on type
  const routes = useMemo(() => {
    if (entityType === 'person') {
      return {
        list: `/api/people/${entityId}/projects`,
        search: `/api/compass_projects`,              // ?search=
        add: `/api/project_staff_link`,               // { project_id, person_id }
        remove: `/api/project_staff_link`,            // { project_id, person_id }
        foreignKey: 'person_id',
        otherKey: 'project_id',
      };
    }
    // entityType === 'proposal'
    return {
      list: `/api/proposalDashboard/${entityId}/projects`,
      search: `/api/compass_projects`,                // ?search=
      add: `/api/proposals/proposal_project_link`,              // { project_id, proposal_id }
      remove: `/api/proposals/proposal_project_link`,           // { project_id, proposal_id }
      foreignKey: 'proposal_id',
      otherKey: 'project_id',
    };
  }, [entityType, entityId]);

  const clearNotice = () => { setError(null); setInfo(null); };

  const fetchLinked = useCallback(async () => {
    if (!entityId) return;
    clearNotice();
    setLoading(true);
    try {
      // Expect API to return an array of projects:
      // { id, title, project_number, client_name, status, ... }
      const { data } = await axios.get(routes.list);
      const list = Array.isArray(data) ? data : (data?.projects || []);
      setLinked(list);
    } catch (e) {
      setError(`Failed to load linked projects: ${e?.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [axios, routes.list, entityId]);

  useEffect(() => { fetchLinked(); }, [fetchLinked]);

  // ---- TagPicker helpers (search projects to add) ----
  const projectToPickerItem = (p) => ({
    key: p.id,
    name: p.title
      ? `${p.title} • ${p.project_number || '—'}`
      : (p.project_number || p.id),
    _raw: p,
  });

  const onFilterChanged = async (filterText) => {
    if (!filterText || !filterText.trim()) return [];
    if (debounceRef.current) clearTimeout(debounceRef.current);

    return new Promise((resolve) => {
      debounceRef.current = setTimeout(async () => {
        try {
          const { data } = await axios.get(routes.search, {
            params: { search: filterText, limit: 15 },
          });
          const items = (Array.isArray(data.data) ? data.data : data?.projects || []).map(projectToPickerItem);

          // Exclude already-linked + already-selected
          const linkedIds = new Set(linked.map((p) => p.id));
          resolve(items.filter((it) => !linkedIds.has(it.key) && !toAdd.some((sel) => sel.key === it.key)));
        } catch {
          resolve([]); // swallow search errors for UX
        }
      }, 250);
    });
  };

  const onItemSelected = (item) => {
    if (linked.some((p) => p.id === item.key) || toAdd.some((t) => t.key === item.key)) return null;
    return item;
  };

  // ---- Add project links ----
  const handleAdd = async () => {
    if (!toAdd.length) return;
    setBusy(true);
    clearNotice();

    try {
      const payloads = toAdd.map((t) => ({
        [routes.otherKey]: t.key,
        [routes.foreignKey]: entityId,
      }));

      // Optimistic UI
      const optimistic = [
        ...linked,
        ...toAdd.map((t) => t._raw || { id: t.key, title: t.name, project_number: '', status: '' }),
      ];
      setLinked(optimistic);

      // POST each link (or batch if supported)
      await Promise.all(payloads.map((p) => axios.post(routes.add, p)));

      setInfo(`Added ${toAdd.length} project${toAdd.length > 1 ? 's' : ''}.`);
      setToAdd([]);
      await fetchLinked(); // refresh to stay canonical
    } catch (e) {
      setError(`Failed to add: ${e?.response?.data?.message || e.message}`);
      await fetchLinked(); // rollback
    } finally {
      setBusy(false);
    }
  };

  // ---- Remove a link ----
  const handleRemove = async (project) => {
    setBusy(true);
    clearNotice();
    try {
      // optimistic remove
      setLinked((cur) => cur.filter((p) => p.id !== project.id));

      await axios.delete(routes.remove, {
        data: {
          [routes.otherKey]: project.id,
          [routes.foreignKey]: entityId,
        },
      });

      setInfo(`Removed “${project.title || project.project_number || project.id}”.`);
    } catch (e) {
      setError(`Failed to remove: ${e?.response?.data?.message || e.message}`);
      await fetchLinked(); // rollback
    } finally {
      setBusy(false);
    }
  };

  // ---- Table columns ----
  const columns = useMemo(() => ([
    {
      key: 'col-title',
      name: 'Project',
      fieldName: 'title',
      minWidth: 220,
      // maxWidth: 420,
      isResizable: true,
      onRender: (item) => (
        <Link to={`/projects/${item.id}`}>{item.title}</Link>
      ),
    },
    {
      key: 'col-actions',
      name: 'Actions',
      minWidth: 90,
      maxWidth: 110,
      onRender: (item) => (
        <TooltipHost content="Remove link">
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Remove"
            ariaLabel="Remove"
            disabled={busy}
            onClick={() => handleRemove(item)}
          />
        </TooltipHost>
      ),
    },
  ]), [busy, theme.palette.neutralSecondary]);

  const manageSubText =
    entityType === 'person'
      ? 'Add or remove projects linked to this person.'
      : 'Add or remove projects linked to this proposal.';

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant='xLarge'>Linked Projects</Text>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <DefaultButton 
              text="Manage"
              onClick={() => setIsManageOpen(true)} />
          </Stack>
        </Stack>
        <Separator />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {info && (
        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setInfo(null)}>
          {info}
        </MessageBar>
      )}

      {loading ? (
        <Stack horizontalAlign="center" styles={{ root: { padding: 24 } }}>
          <Spinner label="Loading linked projects..." />
        </Stack>
      ) : linked.length === 0 ? (
        <Text variant="small" className={styles.smallNote}>No projects linked.</Text>
      ) 
      : (
        <DetailsList
          items={linked}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          compact
        />
      )}

      <Dialog
        hidden={!isManageOpen}
        onDismiss={() => setIsManageOpen(false)}
        minWidth={640}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Manage Project Links',
          subText: manageSubText,
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text variant="smallPlus">Search and select projects to add</Text>
          <TagPicker
            onResolveSuggestions={onFilterChanged}
            onEmptyResolveSuggestions={() => []}
            getTextFromItem={(it) => it.name}
            pickerSuggestionsProps={{ suggestionsHeaderText: 'Projects', noResultsFoundText: 'No matches' }}
            selectedItems={toAdd}
            onChange={setToAdd}
            onItemSelected={onItemSelected}
            inputProps={{ placeholder: 'Type to search projects…' }}
          />
          <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 8 } }}>
            <PrimaryButton
              text={`Add ${toAdd.length ? `(${toAdd.length})` : ''}`}
              onClick={handleAdd}
              disabled={!toAdd.length || busy}
            />
            <DefaultButton text="Close" onClick={() => setIsManageOpen(false)} />
          </Stack>

          <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginTop: 16 } }}>
            <Text variant="smallPlus" styles={{ root: { color: theme.palette.neutralSecondary } }}>
              Currently linked
            </Text>
            <DetailsList
              items={linked}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              compact
            />
          </Stack>
        </Stack>
        <DialogFooter />
      </Dialog>
    </Stack>
  );
};

export default ProjectLinkManager;
